<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Spinner from 'next/spinner/Spinner.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  bot: { type: Object, required: true },
  botId: { type: Number, required: true },
});

const emit = defineEmits(['navigate-to']);

const store = useStore();
const { t } = useI18n();

const results = computed(() => store.getters['wgptBotEvalResults/getResults']);
const recommendations = computed(
  () => store.getters['wgptBotEvalRecommendations/getRecommendations']
);
const resultsMeta = computed(() => store.getters['wgptBotEvalResults/getMeta']);
const resultsUIFlags = computed(
  () => store.getters['wgptBotEvalResults/getUIFlags']
);
const recommendationsUIFlags = computed(
  () => store.getters['wgptBotEvalRecommendations/getUIFlags']
);

const isLoading = computed(
  () =>
    resultsUIFlags.value.isFetching || recommendationsUIFlags.value.isFetching
);

const averageScore = computed(() => {
  if (!results.value.length) return 0;
  const sum = results.value.reduce((acc, r) => acc + (r.overall_score || 0), 0);
  return Math.round(sum / results.value.length);
});

const criticalIssuesCount = computed(
  () => results.value.filter(r => r.has_critical_issues).length
);

const scoreColor = computed(() => {
  if (averageScore.value >= 70) return 'text-n-teal-11';
  if (averageScore.value >= 50) return 'text-n-amber-11';
  return 'text-n-ruby-11';
});

const scoreBarColor = computed(() => {
  if (averageScore.value >= 70) return 'bg-n-teal-6';
  if (averageScore.value >= 50) return 'bg-n-amber-6';
  return 'bg-n-ruby-6';
});

const scoreHealth = computed(() => {
  if (!results.value.length) return null;
  if (averageScore.value >= 70)
    return t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.HEALTH_GOOD');
  if (averageScore.value >= 50)
    return t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.HEALTH_FAIR');
  return t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.HEALTH_POOR');
});

const evalEnabled = computed(() => props.bot.eval_enabled);

onMounted(() => {
  store.dispatch('wgptBotEvalResults/get', { botId: props.botId });
  store.dispatch('wgptBotEvalRecommendations/get', { botId: props.botId });
});
</script>

<template>
  <div class="flex flex-col gap-4 pb-8">
    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <Spinner />
    </div>

    <template v-else-if="evalEnabled">
      <div class="grid grid-cols-2 gap-4">
        <!-- Score Card - featured -->
        <div class="flex flex-col gap-4 p-5 rounded-xl border border-n-weak">
          <div class="flex items-center gap-2">
            <div
              class="size-8 rounded-lg bg-n-alpha-3 flex items-center justify-center"
            >
              <Icon icon="i-lucide-gauge" class="size-4 text-n-slate-11" />
            </div>
            <p class="text-xs text-n-slate-11">
              {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.AVG_SCORE') }}
            </p>
          </div>
          <div class="flex items-end gap-2">
            <p class="text-5xl font-bold leading-none" :class="scoreColor">
              {{ averageScore }}
            </p>
            <p class="text-sm text-n-slate-10 mb-1">
              {{
                t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.SCORE_VALUE', {
                  score: '',
                }).trim()
              }}
            </p>
            <span
              v-if="scoreHealth"
              class="text-xs font-medium mb-1 ml-1"
              :class="scoreColor"
            >
              {{ scoreHealth }}
            </span>
          </div>
          <div class="h-1.5 rounded-full bg-n-alpha-3 overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="scoreBarColor"
              :style="{ width: `${averageScore}%` }"
            />
          </div>
        </div>

        <!-- Right column: two stacked stats -->
        <div class="flex flex-col gap-4">
          <!-- Total Evaluated -->
          <div
            class="flex items-center justify-between p-4 rounded-xl border border-n-weak cursor-pointer hover:bg-n-alpha-1 transition-colors group flex-1"
            @click="emit('navigate-to', 'evaluations')"
          >
            <div class="flex items-center gap-3">
              <div
                class="size-8 rounded-lg bg-n-alpha-3 flex items-center justify-center"
              >
                <Icon
                  icon="i-lucide-message-square-text"
                  class="size-4 text-n-slate-11"
                />
              </div>
              <div>
                <p class="text-xl font-bold text-n-slate-12">
                  {{ resultsMeta.totalCount || 0 }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{
                    t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.TOTAL_EVALUATED')
                  }}
                </p>
              </div>
            </div>
            <Icon
              icon="i-lucide-arrow-right"
              class="size-4 text-n-slate-9 group-hover:text-n-slate-11 transition-colors"
            />
          </div>

          <!-- Critical Issues -->
          <div
            class="flex items-center justify-between p-4 rounded-xl border transition-colors cursor-pointer hover:bg-n-alpha-1 group flex-1"
            :class="
              criticalIssuesCount > 0 ? 'border-n-ruby-6' : 'border-n-weak'
            "
            @click="emit('navigate-to', 'evaluations')"
          >
            <div class="flex items-center gap-3">
              <div
                class="size-8 rounded-lg flex items-center justify-center"
                :class="
                  criticalIssuesCount > 0 ? 'bg-n-ruby-3' : 'bg-n-alpha-3'
                "
              >
                <Icon
                  icon="i-lucide-alert-triangle"
                  class="size-4"
                  :class="
                    criticalIssuesCount > 0
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-11'
                  "
                />
              </div>
              <div>
                <p
                  class="text-xl font-bold"
                  :class="
                    criticalIssuesCount > 0
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ criticalIssuesCount }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{
                    t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.CRITICAL_ISSUES')
                  }}
                </p>
              </div>
            </div>
            <Icon
              icon="i-lucide-arrow-right"
              class="size-4 text-n-slate-9 group-hover:text-n-slate-11 transition-colors"
            />
          </div>
        </div>
      </div>

      <!-- Active Recommendations - wide row -->
      <div
        class="flex items-center justify-between p-4 rounded-xl border border-n-weak cursor-pointer hover:bg-n-alpha-1 transition-colors group"
        @click="emit('navigate-to', 'recommendations')"
      >
        <div class="flex items-center gap-3">
          <div
            class="size-9 rounded-lg bg-n-alpha-3 flex items-center justify-center"
          >
            <Icon icon="i-lucide-lightbulb" class="size-4 text-n-slate-11" />
          </div>
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{
                t(
                  'WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.ACTIVE_RECOMMENDATIONS'
                )
              }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{
                t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.RECOMMENDATIONS_HINT')
              }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <span
            class="text-2xl font-bold"
            :class="
              recommendations.length > 0 ? 'text-n-amber-11' : 'text-n-slate-12'
            "
          >
            {{ recommendations.length }}
          </span>
          <Icon
            icon="i-lucide-arrow-right"
            class="size-4 text-n-slate-9 group-hover:text-n-slate-11 transition-colors"
          />
        </div>
      </div>
    </template>

    <div v-else class="flex flex-col items-center justify-center py-16 gap-2">
      <Icon icon="i-lucide-shield-check" class="size-8 text-n-slate-9" />
      <p class="text-sm text-n-slate-11">
        {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.EVAL_DISABLED') }}
      </p>
    </div>
  </div>
</template>
